import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Permissoes, MenuPermission, ModulePermission } from '../types/Permission';

export const usePermissions = () => {
  const { userProfile } = useAuth();

  const permissoes = useMemo<Permissoes | null>(() => {
    return userProfile?.permissoes || null;
  }, [userProfile?.permissoes]);

  // Função para normalizar chaves (mesma lógica do PerfilAcessoModal)
  const normalizeKey = (path: string): string => {
    if (path === '/') return 'root'; // Tratar a raiz como 'root'
    return path.replace(/[\/\[\]#\$\.\s]/g, '_').replace(/^_+|_+$/g, '');
  };

  const hasMenuAccess = (menuPath: string): boolean => {
    // Se não há permissões configuradas, permitir acesso (compatibilidade)
    if (!permissoes || !permissoes.menus || Object.keys(permissoes.menus).length === 0) {
      return true;
    }
    
    // Normalizar chave para buscar permissões
    const normalizedKey = normalizeKey(menuPath);
    const menuPermission = permissoes.menus[normalizedKey];
    
    // Se o menu não está nas permissões, permitir acesso (compatibilidade)
    if (!menuPermission) return true;
    
    return menuPermission.acesso === true;
  };

  const hasSubmenuAccess = (menuPath: string, submenuPath: string): boolean => {
    // Se não há permissões configuradas, permitir acesso (compatibilidade)
    if (!permissoes || !permissoes.menus || Object.keys(permissoes.menus).length === 0) {
      return true;
    }
    
    // Normalizar chaves
    const normalizedMenuKey = normalizeKey(menuPath);
    const normalizedSubmenuKey = normalizeKey(submenuPath);
    
    const menuPermission = permissoes.menus[normalizedMenuKey];
    if (!menuPermission?.acesso) return false;
    
    // Se o submenu não está nas permissões, permitir acesso se o menu pai tem acesso
    if (!menuPermission.submenus || !menuPermission.submenus[normalizedSubmenuKey]) {
      return menuPermission.acesso === true;
    }
    
    return menuPermission.submenus[normalizedSubmenuKey]?.acesso === true;
  };

  const canCreate = (modulo: string): boolean => {
    // Se não há permissões configuradas, permitir acesso (compatibilidade)
    if (!permissoes || !permissoes.modulos || Object.keys(permissoes.modulos).length === 0) {
      return true;
    }
    
    const modulePermission = permissoes.modulos[modulo];
    // Se o módulo não está nas permissões, permitir acesso (compatibilidade)
    if (!modulePermission) return true;
    
    return modulePermission.criar === true;
  };

  const canEdit = (modulo: string): boolean => {
    // Se não há permissões configuradas, permitir acesso (compatibilidade)
    if (!permissoes || !permissoes.modulos || Object.keys(permissoes.modulos).length === 0) {
      return true;
    }
    
    const modulePermission = permissoes.modulos[modulo];
    // Se o módulo não está nas permissões, permitir acesso (compatibilidade)
    if (!modulePermission) return true;
    
    return modulePermission.editar === true;
  };

  const canDelete = (modulo: string): boolean => {
    // Se não há permissões configuradas, permitir acesso (compatibilidade)
    if (!permissoes || !permissoes.modulos || Object.keys(permissoes.modulos).length === 0) {
      return true;
    }
    
    const modulePermission = permissoes.modulos[modulo];
    // Se o módulo não está nas permissões, permitir acesso (compatibilidade)
    if (!modulePermission) return true;
    
    return modulePermission.excluir === true;
  };

  const canView = (modulo: string): boolean => {
    // Se não há permissões configuradas, permitir acesso (compatibilidade)
    if (!permissoes || !permissoes.modulos || Object.keys(permissoes.modulos).length === 0) {
      return true;
    }
    
    const modulePermission = permissoes.modulos[modulo];
    // Se o módulo não está nas permissões, permitir acesso (compatibilidade)
    if (!modulePermission) return true;
    
    return modulePermission.visualizar === true;
  };

  const getModuleActions = (modulo: string): {
    criar: boolean;
    editar: boolean;
    excluir: boolean;
    visualizar: boolean;
  } => {
    // Se não há permissões configuradas, permitir todas as ações (compatibilidade)
    if (!permissoes || !permissoes.modulos || Object.keys(permissoes.modulos).length === 0) {
      return { criar: true, editar: true, excluir: true, visualizar: true };
    }
    
    const modulePermission = permissoes.modulos[modulo];
    // Se o módulo não está nas permissões, permitir todas as ações (compatibilidade)
    if (!modulePermission) {
      return { criar: true, editar: true, excluir: true, visualizar: true };
    }
    
    return {
      criar: modulePermission.criar || false,
      editar: modulePermission.editar || false,
      excluir: modulePermission.excluir || false,
      visualizar: modulePermission.visualizar || false
    };
  };

  const getMenuActions = (menuPath: string): {
    criar?: boolean;
    editar?: boolean;
    excluir?: boolean;
    visualizar?: boolean;
  } | null => {
    if (!permissoes || !permissoes.menus) return null;
    
    // Normalizar chave para buscar permissões
    const normalizedKey = normalizeKey(menuPath);
    const menuPermission = permissoes.menus[normalizedKey];
    return menuPermission?.acoes || null;
  };

  return {
    permissoes,
    hasMenuAccess,
    hasSubmenuAccess,
    canCreate,
    canEdit,
    canDelete,
    canView,
    getModuleActions,
    getMenuActions
  };
};

